import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FiAlertTriangle, FiTrash2, FiInfo, FiX } from 'react-icons/fi';

const VARIANTS = {
  danger: {
    Icon: FiTrash2,
    accent: 'bg-sky-500 text-white',
    iconBg: 'bg-sky-50',
    iconColor: 'text-sky-500',
    btn: 'bg-sky-500 hover:bg-sky-600 text-white shadow-md shadow-sky-500/20',
  },
  warning: {
    Icon: FiAlertTriangle,
    accent: 'bg-sky-500 text-white',
    iconBg: 'bg-sky-50',
    iconColor: 'text-sky-500',
    btn: 'bg-sky-500 hover:bg-sky-600 text-white shadow-md shadow-sky-500/20',
  },
  info: {
    Icon: FiInfo,
    accent: 'bg-sky-500 text-white',
    iconBg: 'bg-sky-50',
    iconColor: 'text-sky-500',
    btn: 'bg-sky-500 hover:bg-sky-600 text-white shadow-md shadow-sky-500/20',
  },
};

export default function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title = 'Are you sure?',
  message = 'This action cannot be undone.',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger',
  loading = false,
  confirmDisabled = false,
  maxWidth = 'max-w-[620px]',
}) {
  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handle = (e) => { if (e.key === 'Escape' && !loading) onClose(); };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, [isOpen, loading, onClose]);

  if (!isOpen) return null;

  const v = VARIANTS[variant] || VARIANTS.danger;
  const { Icon } = v;

  const modalContent = (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
      {/* Overlay - NO backdrop blur, clean neutral overlay */}
      <div
        className="fixed inset-0 bg-slate-900/40"
        onClick={!loading ? onClose : undefined}
      />

      {/* Dialog */}
      <div
        className={`relative w-full ${maxWidth} max-h-[85vh] flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200 z-10`}
        style={{ animation: 'modalPop 0.2s cubic-bezier(0.16, 1, 0.3, 1)' }}
      >
        <div className="px-6 pt-6 pb-4 overflow-y-auto">
          <div className="flex items-start justify-between mb-4">
            {/* Icon */}
            <div className={`w-12 h-12 rounded-full ${v.iconBg} flex items-center justify-center shrink-0`}>
              <Icon size={22} className={v.iconColor} />
            </div>

            {/* Close */}
            <button
              onClick={onClose}
              disabled={loading}
              className="w-8 h-8 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-colors disabled:opacity-40 cursor-pointer"
            >
              <FiX size={18} />
            </button>
          </div>

          <h3 className="text-xl font-bold text-slate-900 mb-2">{title}</h3>
          <div className="text-sm text-slate-600 leading-relaxed">{message}</div>
        </div>

        <div className="flex gap-3 px-6 pb-6 pt-2">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 h-11 rounded-xl border border-slate-300 bg-white text-slate-700 text-sm font-semibold hover:border-slate-400 hover:bg-slate-50 transition-all disabled:opacity-40 cursor-pointer"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading || confirmDisabled}
            className={`flex-1 h-11 rounded-xl text-sm font-semibold transition-all disabled:opacity-60 flex items-center justify-center cursor-pointer ${v.btn}`}
          >
            {loading ? (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
            ) : confirmText}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes modalPop {
          from { opacity: 0; transform: scale(0.95); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : modalContent;
}
